import { HttpClient, HttpEventType } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FeedbackRoundAsset,
  FeedbackRoundService,
  FilesService,
  RoundAssetSource,
  RoundAssetType,
} from '@brafton/feedback-api';
import { Observable, filter, map, of, switchMap, tap } from 'rxjs';
import { CurrentUserService } from '../../core/current-user.service';

/** The asset types offered, labeled as people say them. */
export const ROUND_ASSET_TYPE_OPTIONS: { value: RoundAssetType; label: string }[] = [
  { value: RoundAssetType.Copy, label: 'Copy doc' },
  { value: RoundAssetType.Drd, label: 'DRD' },
  { value: RoundAssetType.Other, label: 'Other' },
];

/** Documents people attach: PDFs, Office files, and plain text or CSV. */
const ACCEPTED_FILE_TYPES = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.md';

// A presigned single PUT to S3 caps an object at 5 GB.
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024 * 1024;

/**
 * Slide-over drawer for adding an asset to a round: pick what it is (copy doc / DRD /
 * other), then paste a link — usually a Google Doc or Sheet — or upload a file.
 * Uploads go straight to S3 through a signed URL, like mockups; the asset is then
 * recorded on the round. Emits `added` with the stored asset.
 */
@Component({
  selector: 'app-add-asset-drawer',
  templateUrl: './add-asset-drawer.html',
  styleUrl: './add-asset-drawer.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'onClose()',
  },
})
export class AddAssetDrawer {
  /** Whether the drawer is shown. */
  readonly open = input<boolean>(false);

  /** The round the asset belongs to. */
  readonly feedbackRoundId = input<string>('');

  /** Emitted with the stored asset once it has been added. */
  readonly added = output<FeedbackRoundAsset>();

  /** Emitted when the user closes the drawer (button, overlay, or Escape). */
  readonly close = output<void>();

  private readonly rounds = inject(FeedbackRoundService);
  private readonly files = inject(FilesService);
  private readonly http = inject(HttpClient);
  private readonly currentUser = inject(CurrentUserService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly assetTypeOptions = ROUND_ASSET_TYPE_OPTIONS;
  protected readonly acceptedFileTypes = ACCEPTED_FILE_TYPES;

  protected readonly assetType = signal<RoundAssetType>(RoundAssetType.Copy);
  protected readonly source = signal<RoundAssetSource>('link');
  protected readonly linkUrl = signal('');
  protected readonly title = signal('');
  protected readonly chosenFile = signal<File | null>(null);

  protected readonly saving = signal(false);
  /** Upload progress, 0–100, while a file is being sent. */
  protected readonly uploadProgress = signal<number | null>(null);
  protected readonly errorMessage = signal<string | null>(null);

  /** A link must be a web address; an upload needs a file within the size limit. */
  protected readonly canAdd = computed(() => {
    if (this.saving()) {
      return false;
    }
    if (this.source() === 'link') {
      return /^https?:\/\/\S+$/i.test(this.linkUrl().trim());
    }
    const chosenFile = this.chosenFile();
    return chosenFile !== null && chosenFile.size <= MAX_UPLOAD_BYTES;
  });

  constructor() {
    // Start fresh every time the drawer closes.
    effect(() => {
      if (!this.open()) {
        untracked(() => this.reset());
      }
    });
  }

  protected setSource(nextSource: RoundAssetSource): void {
    this.source.set(nextSource);
    this.errorMessage.set(null);
  }

  protected onFileSelected(event: Event): void {
    const fileInput = event.target as HTMLInputElement;
    const selectedFile = fileInput.files?.[0] ?? null;
    fileInput.value = '';
    this.chosenFile.set(selectedFile);
    this.errorMessage.set(selectedFile && selectedFile.size > MAX_UPLOAD_BYTES ? 'Files can be up to 5 GB.' : null);
  }

  protected add(): void {
    if (!this.canAdd()) {
      return;
    }
    const author = this.currentUser.author();
    if (!author.name || !author.email) {
      this.errorMessage.set('Your profile has no name or email, so the asset cannot be attributed.');
      return;
    }
    const roundId = this.feedbackRoundId();
    const chosenFile = this.chosenFile();
    const typedTitle = this.title().trim();
    this.saving.set(true);
    this.errorMessage.set(null);

    const assetUrl$: Observable<string> =
      this.source() === 'upload' && chosenFile ? this.uploadFile(roundId, chosenFile) : of(this.linkUrl().trim());

    assetUrl$
      .pipe(
        switchMap((assetUrl) =>
          this.rounds.addAsset(roundId, {
            assetType: this.assetType(),
            url: assetUrl,
            // An upload is named after its file unless a title was typed.
            title: typedTitle || (this.source() === 'upload' ? chosenFile?.name : undefined),
            source: this.source(),
            addedBy: { name: author.name as string, email: author.email as string },
          }),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (storedAsset) => {
          this.saving.set(false);
          this.added.emit(storedAsset);
          this.close.emit();
        },
        error: () => {
          this.saving.set(false);
          this.uploadProgress.set(null);
          this.errorMessage.set(
            this.source() === 'upload' ? 'Could not upload the file. Please try again.' : 'Could not add the link. Please try again.',
          );
        },
      });
  }

  protected onClose(): void {
    if (this.open() && !this.saving()) {
      this.close.emit();
    }
  }

  /** Signs an upload URL, PUTs the file to S3 with progress, and resolves to its object URL. */
  private uploadFile(roundId: string, fileToUpload: File): Observable<string> {
    const contentType = fileToUpload.type || 'application/octet-stream';
    const storedFilename = `assets/${Date.now()}-${fileToUpload.name.replace(/[^\w.\-]+/g, '_')}`;
    this.uploadProgress.set(0);

    return this.files.getSignedUrl({ feedbackRoundId: roundId, filename: storedFilename, contentType }).pipe(
      switchMap((signedUrl) =>
        this.http
          .put(signedUrl, fileToUpload, { headers: { 'Content-Type': contentType }, reportProgress: true, observe: 'events' })
          .pipe(
            tap((uploadEvent) => {
              if (uploadEvent.type === HttpEventType.UploadProgress && uploadEvent.total) {
                this.uploadProgress.set(Math.round((uploadEvent.loaded / uploadEvent.total) * 100));
              }
            }),
            filter((uploadEvent) => uploadEvent.type === HttpEventType.Response),
            // The stored object is the signed URL without its signature.
            map(() => signedUrl.split('?')[0]),
          ),
      ),
    );
  }

  private reset(): void {
    this.assetType.set(RoundAssetType.Copy);
    this.source.set('link');
    this.linkUrl.set('');
    this.title.set('');
    this.chosenFile.set(null);
    this.saving.set(false);
    this.uploadProgress.set(null);
    this.errorMessage.set(null);
  }
}
